import * as THREE from "three";
import * as RuntimeRegistry from "../../runtime/objectRegistry";
import { getSceneState } from "../../stores/sceneStore";
import type { BodyUUID } from "../../types/scene";
import { logger } from "../../utils/logger";
import { sanitizeMjcfName } from "../../utils/mjcfNames";
import { isFiniteVector3Array } from "./runtimeGuards";
import type { BodyRecord } from "./types";

export type RuntimeBodyResolver = ReturnType<typeof createRuntimeBodyResolver>;

export function createRuntimeBodyResolver() {
  const warnedMissingRuntime = new Set<string>();

  const resolveRuntimeObject = (uuid: BodyUUID): THREE.Object3D | null => {
    const registry = RuntimeRegistry.getAll() || {};
    const obj =
      (registry[uuid] as THREE.Object3D | undefined) ||
      (registry[sanitizeMjcfName(uuid)] as THREE.Object3D | undefined) ||
      undefined;
    return obj || null;
  };

  const resolveBodyWorldPositionScene = (uuid: BodyUUID): THREE.Vector3 | null => {
    const obj = resolveRuntimeObject(uuid);
    if (!obj) return null;
    const p = new THREE.Vector3();
    obj.getWorldPosition(p);
    return p;
  };

  const resolveBodyWorldPositionSceneWithFallback = (uuid: BodyUUID): THREE.Vector3 | null => {
    const runtime = resolveBodyWorldPositionScene(uuid);
    if (runtime) return runtime;
    // Fallback to store position for root bodies. This is less accurate than runtime world position
    // but avoids stalling the pick/place state machine when a runtime object isn't registered.
    const s = getSceneState();
    const entry = ((s?.bodies || {}) as BodyRecord)[uuid] || null;
    const pos = Array.isArray(entry?.position) ? entry.position : null;
    if (isFiniteVector3Array(pos)) {
      if (!warnedMissingRuntime.has(String(uuid))) {
        warnedMissingRuntime.add(String(uuid));
        logger.warn("[ik] Runtime object missing; falling back to store position for target", {
          uuid,
          name: entry?.name || null,
        });
      }
      return new THREE.Vector3(pos[0], pos[1], pos[2]);
    }
    return null;
  };

  return {
    resolveRuntimeObject,
    resolveBodyWorldPositionScene,
    resolveBodyWorldPositionSceneWithFallback,
  };
}
